import { CommandDefinition, defineCommand } from '../../../concepts/commands/CommandDefinition';
import { ComponentTuple, ObjectManager, component, property } from '../../../concepts/objects';
import { memoUsing } from '../../../concepts/objects/MemoUsing';
import { Host } from '../../../concepts/objects/ObjectSymbols';
import { Path } from '../../../platform/geom/Path';
import { Rect } from '../../../platform/geom/Rect';
import { Colors } from '../../../ui';
import { AdapterInfo, CreateAdapterInfo } from '../../canvas/AdapterInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { resolvePhysical, resolvePhysicals } from '../../canvas/commands/CommandUtil';
import { bringToFront, place, unplace } from '../../canvas/commands/OrderCommands';
import { AutoBringToFront } from '../../canvas/entities/AutoBringToFront';
import { HasCousins } from '../../canvas/entities/HasCousins';
import { Physical, PotentialInteraction, RelativeTo, TransformHints } from '../../canvas/entities/Physical';
import { RectangularPhysical } from '../../canvas/entities/RectangularPhysical';
import { Resizable } from '../../canvas/entities/Resizable';
import { Snappable } from '../../canvas/entities/Snappable';
import { PreferredTextColor } from '../../canvas/entities/TextConventions';
import { UIField } from '../../canvas/entities/UIField';
import { ToolManager } from '../../canvas/tooling/ToolManager';
import { EffectDirector, Effects } from '../../canvas/visualization/EffectDirector';
import { AutoLayout, AutoLayoutMode, AutoLayoutSubject } from '../autoLayout/AutoLayout';
import { AutoLayoutEngine } from '../autoLayout/AutoLayoutEngine';
import { FlexLayout, FlexSorter } from '../autoLayout/FlexLayout';
import { SmartContainer } from '../controls/SmartContainer';
import { ZoneEmojiField } from './ZoneEmojiField';
import { ZoneIndex } from './ZoneIndex';
import { ZoneLayout } from './ZoneLayout';
import { zonePropertyLayout } from './ZonePropertyLayouts';
import { ZoneTitleField } from './ZoneTitleField';

const mutation = defineCommand(
    'zoneMutate',
    null,
    (r, [id]: [string]) => {
        r.call(EditorSession).update(AdapterInfo, CreateAdapterInfo(id, 'field'));
    },
    [],
);

const repeat = defineCommand(
    'zoneRepeat',
    null,
    (r, [id]: [string]) => {
        const objects = r.call(ObjectManager);
        const zone = objects.get(id, Zone);
        const tools = r.call(ToolManager);
        tools.equip('zoneTool', {
            color: zone.autoColor,
            emoji: zone.emoji,
        });
    },
);

const pipe = defineCommand(
    'zonePipe',
    null,
    (cc, [target, elmts]: [any, any]) => {

        const autoLayout = cc.call(AutoLayoutEngine);
        const objects = cc.call(ObjectManager);
        const effects = cc.call(EffectDirector);

        const zone = resolvePhysical(cc, target)?.as(Zone);
        const subjects = resolvePhysicals(cc, elmts);

        if (!subjects.length) return;

        objects.update(() => {

            cc.dispatch(unplace, [subjects]);
            cc.dispatch(bringToFront, [subjects.map(x => x.id), true]);

            const from = new Map(
                subjects.map(x => [x, x.transform(RelativeTo.Model)])
            );

            const [axis] = zone.layoutMode;
            if (axis === 'none') {
                FlexLayout.exec(zone.bounds(RelativeTo.Model), ['row', 'center', 'center', 20], subjects);
                cc.dispatch(place, [subjects]);
            }
            else {
                const [cell] = zone.layoutCells(RelativeTo.Model);
                autoLayout.insert(zone, cell.topLeft().add([10, 10]), subjects);
            }

            const to = new Map(
                subjects.map(x => [x, x.transform(RelativeTo.Model)])
            );

            for (const s of subjects) {
                effects.trigger(Effects.tween(from.get(s), to.get(s)), s);
            }
        });
    }
);

const zoneDef = {
    name: 'Zone',
    implements: [Physical, Resizable, AutoLayout, HasCousins, PreferredTextColor, Snappable, SmartContainer],
    mutation,
    propertyLayout: zonePropertyLayout,
};

@component(zoneDef)
export class Zone extends RectangularPhysical implements AutoLayout, HasCousins, PreferredTextColor, SmartContainer, Snappable {

    @property({ type: 'string', style: true })
    public autoColor!: string;

    @property({ type: 'string', default: 'white' })
    public background!: string;

    @property({ type: 'number[]', style: true, default: [0, 0, 0, 0] })
    public border!: [number, number, number, number];

    @property({ type: 'number', default: 4 })
    public cornerRadius!: number;

    @property({ type: 'string', name: 'emoji', oldName: 'emoji|value' })
    public emoji!: string;

    @property({ type: 'string', default: 'left', style: true })
    public headingPosition: 'left' | 'center' | 'right';

    @property({ type: 'number', default: 0, style: true })
    public headingOffset: number;

    @property({ type: 'string', default: 'classic', style: true })
    public headingStyle!: 'classic' | 'fill' | 'roundFill' | 'off';

    @property({ type: 'string', oldName: 'title|value' })
    public title!: string;

    @property({ type: 'string', style: true, default: 'Rubik', oldName: 'title|fontFamily' })
    public titleFontFamily!: string;

    @property({ type: 'string', style: true, default: Colors.text, oldName: 'title|fontColor' })
    public titleFontColor!: string;

    @property({ type: 'number', style: true, default: 26, oldName: 'title|fontSize' })
    public titleFontSize!: number;

    @property({ type: 'string[]', default: AutoLayoutMode.none })
    public layoutMode: AutoLayoutMode;

    public get boundary() {
        return false;
    }

    @memoUsing(
        'width', 'height', 'title', 'titleFontFamily', 'titleFontColor', 'titleFontSize',
        'border', 'headingPosition', 'headingOffset', 'headingStyle',
    )
    public get layout() {
        return ZoneLayout(this);
    }

    @memoUsing('layout')
    public get uiFields(): readonly UIField[] {
        return [
            ZoneTitleField(this),
            ZoneEmojiField(this),
        ];
    }

    public get shouldSnap() {
        return true;
    }

    public getPhysicalsNaturallyOrdered(): Physical[] {
        const [axis] = this.layoutMode;
        return FlexSorter.sort(
            (!axis || axis === 'none' ? axis : 'row') as any,
            this.polarize(AutoLayoutSubject)
        );
    }

    public queryPipe(subject: Physical): CommandDefinition<[string, string]> {
        if (subject.is(AutoBringToFront)) {
            return pipe;
        }
        return null;
    }

    public cousins(): Iterable<Physical> {
        return this[Host].resolve(ZoneIndex).lookup(this.id);
    }

    public queryTransformHints() {
        return TransformHints({
            scaleX: true,
            scaleY: true,
            translate: true,
            visible: true,
        });
    }

    public get minWidth() {
        return 220;
    }

    public get minHeight() {
        return 180;
    }

    public layoutCells(to?: RelativeTo): Rect[] {
        const mt = this.transform(to);
        return [Rect.fromPoints(mt.apply(this.layout.content))];
    }

    public path(to?: RelativeTo | Physical): Path {
        return this.outline(to).toRoundedPath('cubic', 4);
    }

    public testInteraction({ mpt }: PotentialInteraction): [boolean, string] {
        const { handle, inner } = this.layout;
        const rpt = this.toRelative(mpt);
        if (this.localBounds.contains(rpt)) {
            if (handle.contains(rpt) || !inner.contains(rpt)) {
                return [true, 'default'];
            }
        }
        return [false, 'none'];
    }

    public wrap(elmts: Physical[]) {

        const all = [
            ...elmts,
            ...this.polarize(Physical)
        ];

        const resets = all.map(x => [x, x.createFigure()] as const);

        const area = Rect.fromMany(all.map(x => x.bounds(RelativeTo.Model)));
        const inflate = this.layout.content.topLeft().add([10, 10]);
        this.setFromFigure(area.inflate(inflate).points());

        elmts.forEach(x => this.add(x.removeSelf()));
        resets.forEach(([x, f]) => x.setFromFigure(f));
    }

    public getPreferredTextColor(): string {
        if (this.headingStyle === 'fill' || this.headingStyle === 'roundFill') {
            return Colors.contrastTextColorFor(this.autoColor);
        }
        else {
            return Colors.contrastTextColorFor('#fff');
        }
    }

    public toText() {
        return this.title;
    }

    public toEmptyTuple(to?: Physical | RelativeTo): ComponentTuple {
        const ct = super.toShallowTuple(to);
        ct[2].title = '';
        return ct;
    }

    public toHumanString(): string {
        return `Zone ("${(this.title).slice(0, 20)}")`;
    }
}
